import { Link } from 'react-router-dom';
import { SectionHeading } from '../components';

const CancellationPolicy = () => {
  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Legal</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Cancellation & Refund Policy</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            We understand plans change. Review our cancellation and refund policy below.
          </p>
        </div>
      </section>

      {/* ===== LAST UPDATED ===== */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-500">Last updated: March 2026</p>
        </div>
      </section>

      {/* ===== SECTION 2: REFUND ELIGIBILITY (PROMINENT) ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 1" title="Refund Eligibility" />
          <div className="prose prose-slate max-w-none">
            {/* 90-day callout box */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6 my-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-amber-900 font-bold text-lg mb-1">90-Day Full Refund Window</p>
                  <p className="text-amber-800">
                    Full refunds are available for cancellation requests made <strong>90 or more days before the event start date</strong>. After the 90-day window, refunds may be partial or unavailable depending on timing and costs already incurred.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed">
              We want every family member to feel confident when registering. If your plans change, our refund policy is designed to be fair and transparent. The earlier you notify us, the more likely a full refund can be processed.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: HOW TO REQUEST A CANCELLATION ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 2" title="How to Request a Cancellation" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              To request a cancellation, follow these steps:
            </p>
            <ol className="space-y-3 text-slate-600 mt-4">
              <li><strong className="text-slate-900">Log in</strong> to your account on this website</li>
              <li>Navigate to the <strong className="text-slate-900">Account</strong> page</li>
              <li>Use the <strong className="text-slate-900">Cancel Registration</strong> section</li>
              <li>Provide a reason for your cancellation</li>
              <li>You will receive a <strong className="text-slate-900">refund estimate</strong> before confirming your cancellation</li>
            </ol>
            <p className="text-slate-600 leading-relaxed mt-4">
              Once confirmed, you will receive an email acknowledgment with details about your refund amount and timeline.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: REFUND PROCESSING ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 3" title="Refund Processing" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              Once a cancellation is approved, refunds are handled as follows:
            </p>
            <ul className="space-y-3 text-slate-600 mt-4">
              <li>Approved refunds are processed back to the original payment method</li>
              <li>All refunds are processed by <strong className="text-slate-900">KG ProDesign LLC</strong></li>
              <li>Please allow <strong className="text-slate-900">5-10 business days</strong> for the refund to appear on your statement</li>
              <li>The refund will appear as a credit from <strong className="text-slate-900">"KG PRODESIGN LLC"</strong> or <strong className="text-slate-900">"KGP"</strong></li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: SERVICE FEES & PROCESSING FEES ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 4" title="Service Fees & Processing Fees" />
          <div className="prose prose-slate max-w-none">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-4">
              <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Important Disclaimer</h4>
              <ul className="space-y-3 text-slate-600">
                <li>
                  Third-party payment processors and credit card companies may charge service fees or processing fees on transactions. These fees are assessed by the payment provider, not by the reunion organizers or KG ProDesign LLC.
                </li>
                <li>
                  In some cases, processing fees charged by payment gateways may not be refundable, even when a full refund of reunion costs is issued.
                </li>
                <li>
                  KG ProDesign LLC is not responsible for fees charged by your bank or credit card company.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: NON-REFUNDABLE ITEMS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 5" title="Non-Refundable Items" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              Certain costs may be non-refundable if already committed on your behalf, such as specific vendor deposits, pre-ordered merchandise, or other expenses that cannot be recovered.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Any non-refundable items will be clearly communicated to you at the time of your refund estimate, before you confirm your cancellation.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: PAYMENT PROCESSING ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 6" title="Payment Processing" />
          <div className="prose prose-slate max-w-none">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-4">
              <ul className="space-y-2 text-slate-600">
                <li>All payments are managed by <strong className="text-slate-900">KG ProDesign LLC</strong></li>
                <li>KG ProDesign LLC tracks and manages all funds for the reunion budget</li>
                <li>Your credit card statement will show <strong className="text-slate-900">"KG PRODESIGN LLC"</strong> or <strong className="text-slate-900">"KGP"</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: QUESTIONS & CONTACT ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 7" title="Questions & Contact" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              For any questions or concerns about cancellations, refunds, or payments, please contact us:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-6">
              <p className="text-slate-900 font-bold text-lg mb-2">KG ProDesign LLC</p>
              <p className="text-slate-600">
                Website:{' '}
                <a href="https://kgprodesign.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  kgprodesign.com
                </a>
              </p>
              <p className="text-slate-600">
                Email:{' '}
                <a href="mailto:info@kgprodesign.com" className="text-primary hover:underline">
                  info@kgprodesign.com
                </a>
              </p>
              <p className="text-sm text-slate-500 mt-3">
                For payment-related inquiries, please reference "KG PRODESIGN LLC" or "KGP" as it appears on your credit card statement.
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed">
              For general reunion questions, visit our{' '}
              <Link to="/contact" className="text-primary font-medium hover:underline">
                Contact page
              </Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ===== CROSS-LINKS ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/terms"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
            >
              Terms & Conditions
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
            >
              Privacy Policy
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CancellationPolicy;
